// @ts-nocheck
import React, { useEffect, useMemo, useState } from 'react';
import {
  Feather, Plus, BookOpen, Library, Clock, Shield, X, ArrowRight, Download, Trash2, Edit2, Link as LinkIcon,
  Heart, Users, MessageCircle
} from 'lucide-react';
import { Toaster, toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import * as api from './lib/api';
import type { Move } from './lib/types';

// ---------- Types for UI state ----------
interface ThreadUI {
  id: string;
  initial_text: string;
  status: 'open' | 'resting' | 'integrated';
  primary_taxon_id?: string | null;
  primary_taxon_name?: string | null;
  created_at: string;
  updated_at: string;
  reflection_count?: number;
}

interface ReflectionUI {
  id: string;
  content: string;
  moves?: Move | null;
  created_at: string;
}

interface TaxonUI {
  id: string;
  name: string;
  description?: string | null;
  created_at: string;
  thread_count?: number;
  reflection_count?: number;
}

interface ScriptUI {
  id: string;
  title: string;
  body: string;
  taxon_id?: string | null;
  taxon_name?: string | null;
  usage_count: number;
}

interface LoomEventUI {
  id: string;
  event_type: string;
  payload?: any;
  created_at: string;
  thread_id?: string;
  taxon_id?: string;
  thread_initial?: string;
  taxon_name?: string;
}

// ---------- Small presentational helpers ----------
function formatDate(iso: string) {
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return iso; }
}

function formatTime(iso: string) {
  try {
    return new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  } catch { return ''; }
}

function short(text: string, n = 120) {
  if (!text) return '';
  return text.length > n ? text.slice(0, n - 1) + '…' : text;
}

// ---------- Main App ----------
export default function CurioApp() {
  const [token, setToken] = useState<string | null>(null);
  const [me, setMe] = useState<any>(null);
  const [view, setView] = useState<'threads' | 'taxonomy' | 'scripts' | 'loom' | 'privacy'>('threads');
  const [loading, setLoading] = useState(true);

  // Data
  const [threads, setThreads] = useState<ThreadUI[]>([]);
  const [taxa, setTaxa] = useState<TaxonUI[]>([]);
  const [scripts, setScripts] = useState<ScriptUI[]>([]);
  const [loomEvents, setLoomEvents] = useState<LoomEventUI[]>([]);
  const [patterns, setPatterns] = useState<any>(null);
  const [exportsList, setExportsList] = useState<any[]>([]);

  // Current focused thread (the "Curiosity Engine")
  const [activeThreadId, setActiveThreadId] = useState<string | null>(null);
  const [activeThread, setActiveThread] = useState<any>(null);
  const [activeReflections, setActiveReflections] = useState<ReflectionUI[]>([]);

  // Composer states
  const [showNewThread, setShowNewThread] = useState(false);
  const [newThreadText, setNewThreadText] = useState('');
  const [newThreadStatus, setNewThreadStatus] = useState<'open' | 'resting'>('open');

  const [reflectText, setReflectText] = useState('');
  const [showMoves, setShowMoves] = useState(false);
  const [move, setMove] = useState<Partial<Move>>({});

  // Taxon creation / editing
  const [showNewTaxon, setShowNewTaxon] = useState(false);
  const [newTaxonName, setNewTaxonName] = useState('');
  const [newTaxonDesc, setNewTaxonDesc] = useState('');
  const [editingTaxon, setEditingTaxon] = useState<TaxonUI | null>(null);

  // Script creation
  const [showNewScript, setShowNewScript] = useState(false);
  const [newScriptTitle, setNewScriptTitle] = useState('');
  const [newScriptBody, setNewScriptBody] = useState('');
  const [scriptForTaxon, setScriptForTaxon] = useState<string | null>(null);

  // Command palette
  const [cmdOpen, setCmdOpen] = useState(false);
  const [cmdQuery, setCmdQuery] = useState('');

  // Privacy / export
  const [emailInput, setEmailInput] = useState('');
  const [exporting, setExporting] = useState(false);

  // Zen mode for deep reflection
  const [zenThreadId, setZenThreadId] = useState<string | null>(null);

  // Load token + bootstrap on mount
  useEffect(() => {
    (async () => {
      const existing = localStorage.getItem('curio_token');
      try {
        const b = await api.bootstrap(existing || undefined);
        setToken(b.token);
        localStorage.setItem('curio_token', b.token);
        await loadMeAndData(b.token);
      } catch (e) {
        // First ever load — bootstrap will have created
        const b = await api.bootstrap();
        setToken(b.token);
        localStorage.setItem('curio_token', b.token);
        await loadMeAndData(b.token);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  async function loadMeAndData(t?: string) {
    const currentToken = t || token;
    if (!currentToken) return;

    try {
      const meData = await api.getMe();
      setMe(meData);
      setEmailInput(meData.user?.email || '');

      const [th, tx, sc, ex] = await Promise.all([
        api.listThreads(),
        api.listTaxa(),
        api.listScripts(),
        api.listExports(),
      ]);

      setThreads(th.threads || []);
      setTaxa(tx.taxa || []);
      setScripts(sc.scripts || []);
      setExportsList(ex.exports || []);

      // Load loom + patterns in background
      const [loom, pats] = await Promise.all([
        api.getLoom({ limit: 60 }),
        api.getLoomPatterns(),
      ]);
      setLoomEvents(loom.events || []);
      setPatterns(pats);
    } catch (e: any) {
      toast.error('Could not load your data. Refresh or check connection.');
    }
  }

  async function refreshThreads() {
    const th = await api.listThreads();
    setThreads(th.threads || []);
  }

  async function refreshTaxa() {
    const tx = await api.listTaxa();
    setTaxa(tx.taxa || []);
  }

  async function refreshScripts() {
    const sc = await api.listScripts();
    setScripts(sc.scripts || []);
  }

  async function refreshExports() {
    const ex = await api.listExports();
    setExportsList(ex.exports || []);
  }

  // ---------- Tiny action: create thread ----------
  async function handleCreateThread() {
    if (!newThreadText.trim()) return;
    try {
      const { thread } = await api.createThread(newThreadText.trim(), newThreadStatus);
      setThreads(prev => [thread, ...prev]);
      setShowNewThread(false);
      setNewThreadText('');
      setNewThreadStatus('open');
      toast.success('Thread held. You can return to it whenever you are ready.');
      // Immediately open the engine
      openThread(thread.id);
    } catch (e) {
      toast.error('Could not hold the thread. Try again.');
    }
  }

  // ---------- Open the Curiosity Engine ----------
  async function openThread(id: string) {
    try {
      const data = await api.getThread(id);
      setActiveThread(data.thread);
      setActiveReflections(data.reflections || []);
      setActiveThreadId(id);
      setView('threads'); // stay in threads area but show the focused engine
      setReflectText('');
      setMove({});
      setShowMoves(false);
    } catch (e) {
      toast.error('Could not open that thread.');
    }
  }

  function closeEngine() {
    setActiveThreadId(null);
    setActiveThread(null);
    setActiveReflections([]);
  }

  // ---------- Add reflection + optional moves ----------
  async function handleAddReflection() {
    if (!activeThreadId || !reflectText.trim()) return;

    const movesToSend = Object.keys(move).length > 0 ? move : undefined;

    try {
      const res = await api.addReflection(activeThreadId, reflectText.trim(), movesToSend);
      // Optimistic
      setActiveReflections(prev => [...prev, {
        id: res.reflection.id,
        content: res.reflection.content,
        moves: res.reflection.moves,
        created_at: res.reflection.created_at,
      } as ReflectionUI]);

      setReflectText('');
      setMove({});
      setShowMoves(false);

      toast.success(movesToSend ? 'Reflection + move recorded.' : 'Reflection added. The thread is still here when you need it.');

      // Refresh background lists so counts update
      await refreshThreads();
      await loadLoomAndPatterns();
    } catch (e) {
      toast.error('Could not save the reflection.');
    }
  }

  async function loadLoomAndPatterns() {
    try {
      const [loom, pats] = await Promise.all([api.getLoom({ limit: 60 }), api.getLoomPatterns()]);
      setLoomEvents(loom.events || []);
      setPatterns(pats);
    } catch {}
  }

  // ---------- Weave thread into taxon ----------
  async function weaveThread(threadId: string, taxonId: string) {
    try {
      await api.assignThread(threadId, taxonId);
      toast.success('Woven into your taxonomy.');
      if (activeThreadId === threadId) {
        await openThread(threadId); // refresh assignments
      }
      await refreshThreads();
      await refreshTaxa();
      await loadLoomAndPatterns();
    } catch (e) {
      toast.error('Could not weave the thread.');
    }
  }

  // ---------- Create taxon ----------
  async function handleCreateTaxon() {
    if (!newTaxonName.trim()) return;
    try {
      await api.createTaxon(newTaxonName.trim(), newTaxonDesc.trim() || undefined);
      setShowNewTaxon(false);
      setNewTaxonName('');
      setNewTaxonDesc('');
      await refreshTaxa();
      toast.success('New taxon created in your taxonomy.');
    } catch (e) {
      toast.error('Could not create taxon.');
    }
  }

  // ---------- Scripts ----------
  async function handleCreateScript() {
    if (!newScriptTitle.trim() || !newScriptBody.trim()) return;
    try {
      await api.createScript(newScriptTitle.trim(), newScriptBody.trim(), scriptForTaxon || undefined);
      setShowNewScript(false);
      setNewScriptTitle('');
      setNewScriptBody('');
      setScriptForTaxon(null);
      await refreshScripts();
      toast.success('Repair script saved to your library.');
    } catch (e) {
      toast.error('Could not save script.');
    }
  }

  async function handleUseScript(scriptId: string, threadId: string, note?: string) {
    try {
      await api.useScript(scriptId, threadId, note);
      toast.success('Script use logged. This helps patterns appear in the Loom.');
      if (activeThreadId === threadId) {
        await openThread(threadId);
      }
      await refreshScripts();
      await loadLoomAndPatterns();
    } catch (e) {
      toast.error('Could not log the script use.');
    }
  }

  // ---------- Export via Workflow ----------
  async function handleRequestExport() {
    setExporting(true);
    try {
      const res = await api.requestExport();
      toast.success('Export started. A Workflow is building your codex and writing it to private R2 storage.');
      await refreshExports();
      // Poll once after a short delay (Workflows are async)
      setTimeout(async () => {
        await refreshExports();
      }, 2200);
    } catch (e) {
      toast.error('Could not start export.');
    } finally {
      setExporting(false);
    }
  }

  async function handleDownload(exportId: string, file: string) {
    try {
      await api.downloadExport(exportId, file);
    } catch (e) {
      toast.error('Download failed. The export may still be building.');
    }
  }

  async function handleDeleteExport(exportId: string) {
    if (!confirm('Delete this export bundle from R2?')) return;
    try {
      await api.deleteExport(exportId);
      await refreshExports();
      toast.success('Export deleted from your private storage.');
    } catch (e) {
      toast.error('Delete failed.');
    }
  }

  // ---------- Full delete ----------
  async function handleFullDelete() {
    const confirmText = prompt('Type exactly: DELETE ALL MY DATA');
    if (confirmText !== 'DELETE ALL MY DATA') {
      toast.error('Confirmation did not match. Nothing was deleted.');
      return;
    }
    try {
      await api.deleteAllData();
      localStorage.removeItem('curio_token');
      toast.success('All data deleted. The page will reload.');
      setTimeout(() => window.location.reload(), 900);
    } catch (e) {
      toast.error('Delete encountered an issue. Some data may remain — you can try again.');
    }
  }

  // ---------- Keyboard + Command palette ----------
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setCmdOpen(v => !v);
        setCmdQuery('');
      }
      if (e.key === '?' && !['INPUT', 'TEXTAREA'].includes((e.target as HTMLElement)?.tagName)) {
        e.preventDefault();
        setCmdOpen(true);
        setCmdQuery('');
      }
      if (e.key.toLowerCase() === 'n' && !['INPUT', 'TEXTAREA'].includes((e.target as HTMLElement)?.tagName)) {
        e.preventDefault();
        setShowNewThread(true);
        setView('threads');
      }
      if (e.key === 'Escape') {
        if (cmdOpen) setCmdOpen(false);
        else if (showNewThread) setShowNewThread(false);
        else if (activeThreadId) closeEngine();
        else if (zenThreadId) setZenThreadId(null);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [cmdOpen, showNewThread, activeThreadId, zenThreadId]);

  const cmdActions = useMemo(() => {
    const actions = [
      { label: 'New thread (hold a confusion)', action: () => { setCmdOpen(false); setShowNewThread(true); setView('threads'); } },
      { label: 'Go to Threads', action: () => { setCmdOpen(false); setView('threads'); } },
      { label: 'Go to Taxonomy (the artifact)', action: () => { setCmdOpen(false); setView('taxonomy'); } },
      { label: 'Go to Scripts library', action: () => { setCmdOpen(false); setView('scripts'); } },
      { label: 'Go to Loom (your moves over time)', action: () => { setCmdOpen(false); setView('loom'); } },
      { label: 'Go to Privacy & Export', action: () => { setCmdOpen(false); setView('privacy'); } },
      { label: 'Request full private export (Workflow + R2)', action: () => { setCmdOpen(false); setView('privacy'); handleRequestExport(); } },
    ];
    if (!cmdQuery) return actions;
    const q = cmdQuery.toLowerCase();
    return actions.filter(a => a.label.toLowerCase().includes(q));
  }, [cmdQuery]);

  function runCmdAction(a: any) {
    a.action();
  }

  // ---------- Zen / focus mode for a thread ----------
  function enterZen(id: string) {
    setZenThreadId(id);
    if (activeThreadId !== id) openThread(id);
  }

  // ---------- Print a taxon as simple codex ----------
  async function printTaxon(taxon: TaxonUI) {
    try {
      const full = await api.getTaxon(taxon.id);
      const win = window.open('', '_blank');
      if (!win) return;
      const html = `
        <html><head><title>${taxon.name} — curio</title>
        <style>body{font-family:Georgia,serif;max-width:720px;margin:40px auto;padding:0 20px;line-height:1.65;color:#2c2522} h1,h2{border-bottom:1px solid #e6e0d6;padding-bottom:4px} .meta{color:#6b625b;font-size:0.9rem} .thread{margin:1.6rem 0} .refl{margin-left:1rem;border-left:3px solid #d8d0c4;padding-left:1rem}</style>
        </head><body>
        <h1>${taxon.name}</h1>
        <div class="meta">Exported from your personal Confusion Taxonomy • ${new Date().toLocaleDateString()}</div>
        ${taxon.description ? `<p>${taxon.description}</p>` : ''}
        <h2>Threads woven here</h2>
        ${(full.threads || []).map((t: any) => `
          <div class="thread">
            <strong>${formatDate(t.created_at)}</strong><br/>
            <em>${t.initial_text}</em>
            ${(full.scripts || []).filter((s: any) => (full.threads || []).some((tt: any) => tt.id === t.id)).map((s: any) => `<div style="margin:6px 0 0 12px;font-size:0.9rem">Script used: ${s.title}</div>`).join('')}
          </div>
        `).join('')}
        <h2 style="margin-top:2.4rem">Your repair scripts for this pattern</h2>
        ${(full.scripts || []).map((s: any) => `<div style="margin:1rem 0"><strong>${s.title}</strong><br/><pre style="white-space:pre-wrap;font-family:inherit">${s.body}</pre></div>`).join('')}
        <hr style="margin:3rem 0 1rem"/>
        <small style="color:#6b625b">curio — the curiosity engine<br/>https://www.linkedin.com/in/mindful-abhay/ · https://buymeacoffee.com/abhaypratapsingh</small>
        </body></html>`;
      win.document.write(html);
      win.document.close();
      setTimeout(() => win.print(), 240);
    } catch (e) {
      toast.error('Could not prepare print view.');
    }
  }

  // ---------- Render ----------
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f8f4ec]">
        <div className="text-center">
          <div className="text-4xl mb-3">🪶</div>
          <div className="text-xl tracking-tight">curio</div>
          <div className="text-sm text-[#6b625b] mt-1">loading your private space…</div>
        </div>
      </div>
    );
  }

  const activeTaxonForThread = activeThread?.primary_taxon_id
    ? taxa.find(t => t.id === activeThread.primary_taxon_id)
    : null;

  const filteredThreads = threads; // simple — could add filters later

  return (
    <div className="min-h-screen bg-[#f8f4ec] text-[#2c2522]">
      <Toaster position="top-center" richColors closeButton />

      {/* Top bar — public utility, calm, links prominent */}
      <div className="border-b border-[#d8d0c4] bg-white/70 backdrop-blur sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-5 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div onClick={() => { setView('threads'); closeEngine(); }} className="flex items-center gap-2 cursor-pointer">
              <div className="w-7 h-7 rounded bg-[#2c2522] text-white flex items-center justify-center text-sm">🪶</div>
              <div>
                <span className="font-semibold tracking-tight">curio</span>
                <span className="ml-1.5 text-xs text-[#6b625b]">the curiosity engine</span>
              </div>
            </div>
            <div className="hidden sm:block text-[10px] px-2 py-px rounded bg-[#f1ede5] text-[#6b625b] ml-1">for parents of teenagers</div>
          </div>

          <div className="flex items-center gap-2 text-sm">
            <button onClick={() => setView('threads')} className={`px-3 py-1 rounded ${view === 'threads' ? 'bg-[#2c2522] text-white' : 'hover:bg-[#f1ede5]'}`}>Threads</button>
            <button onClick={() => { setView('taxonomy'); closeEngine(); }} className={`px-3 py-1 rounded ${view === 'taxonomy' ? 'bg-[#2c2522] text-white' : 'hover:bg-[#f1ede5]'}`}>Taxonomy</button>
            <button onClick={() => { setView('scripts'); closeEngine(); }} className={`px-3 py-1 rounded ${view === 'scripts' ? 'bg-[#2c2522] text-white' : 'hover:bg-[#f1ede5]'}`}>Scripts</button>
            <button onClick={() => { setView('loom'); closeEngine(); }} className={`px-3 py-1 rounded ${view === 'loom' ? 'bg-[#2c2522] text-white' : 'hover:bg-[#f1ede5]'}`}>Loom</button>
            <button onClick={() => { setView('privacy'); closeEngine(); }} className={`px-3 py-1 rounded ${view === 'privacy' ? 'bg-[#2c2522] text-white' : 'hover:bg-[#f1ede5]'}`}><Shield size={15} className="inline -mt-px mr-1" />Privacy</button>

            <div className="w-px h-5 bg-[#d8d0c4] mx-1" />

            <a href="https://www.linkedin.com/in/mindful-abhay/" target="_blank" rel="noreferrer"
               className="text-[#c17f3a] hover:text-[#9c6644] flex items-center gap-1 text-xs px-2 py-1 rounded hover:bg-[#f1ede5]">
              <Users size={14} /> LinkedIn
            </a>
            <a href="https://buymeacoffee.com/abhaypratapsingh" target="_blank" rel="noreferrer"
               className="text-[#c17f3a] hover:text-[#9c6644] flex items-center gap-1 text-xs px-2 py-1 rounded hover:bg-[#f1ede5]">
              <Heart size={14} /> Buy me a coffee
            </a>

            <button
              onClick={() => setCmdOpen(true)}
              className="ml-2 text-xs px-2.5 py-1 rounded border border-[#d8d0c4] hover:bg-white flex items-center gap-1"
              title="Command palette (⌘K or ?)"
            >
              ⌘K
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-5 pt-8 pb-24">
        {/* Hero / orientation — only on threads when nothing focused */}
        {view === 'threads' && !activeThreadId && (
          <div className="mb-8">
            <div className="max-w-2xl">
              <div className="uppercase tracking-[3px] text-xs text-[#6b625b] mb-2">slow reflection for parents of teenagers</div>
              <h1 className="text-4xl tracking-tighter font-semibold">One small confusion.<br />A lifetime of repair.</h1>
              <p className="mt-3 text-[15px] text-[#5c6652] leading-relaxed">
                Capture a single moment that left you or your teen puzzled or at odds. Return when you can.
                Write what actually happened. Script a repair attempt that starts from curiosity.
                Over time your own <span className="font-medium text-[#2c2522]">Confusion Taxonomy</span> becomes the useful artifact.
              </p>
              <div className="mt-4 flex gap-3">
                <button onClick={() => setShowNewThread(true)} className="btn btn-primary">
                  <Plus size={17} /> Hold a thread
                </button>
                <button onClick={() => setView('taxonomy')} className="btn btn-ghost">See the taxonomy</button>
              </div>
              <div className="mt-2 text-[11px] text-[#6b625b]">Press <span className="font-mono">n</span> anywhere for a new thread. <span className="font-mono">⌘K</span> for everything.</div>
            </div>
          </div>
        )}

        {/* THREADS VIEW + THE CURIOSITY ENGINE */}
        {view === 'threads' && (
          <>
            {/* New thread modal (the single tiny action) */}
            <AnimatePresence>
              {showNewThread && (
                <div className="fixed inset-0 bg-black/30 z-50 flex items-start justify-center pt-20" onClick={() => setShowNewThread(false)}>
                  <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 6 }}
                    onClick={e => e.stopPropagation()}
                    className="modal w-full max-w-xl p-6">
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="text-sm uppercase tracking-widest text-[#6b625b]">Tiny first action</div>
                        <div className="text-2xl tracking-tight mt-1">Hold a thread</div>
                      </div>
                      <button onClick={() => setShowNewThread(false)}><X /></button>
                    </div>

                    <div className="mt-5">
                      <textarea
                        className="textarea"
                        placeholder="She came in from school and I asked one question and the whole evening went sideways. I still don't know what actually happened."
                        value={newThreadText}
                        onChange={e => setNewThreadText(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleCreateThread(); }}
                      />
                      <div className="mt-3 flex items-center gap-3 text-sm">
                        <div className="text-[#6b625b]">Status for now:</div>
                        <button onClick={() => setNewThreadStatus('open')} className={`pill ${newThreadStatus === 'open' ? 'bg-[#f4e9dc] border-[#e8d9c8]' : ''}`}>Open</button>
                        <button onClick={() => setNewThreadStatus('resting')} className={`pill ${newThreadStatus === 'resting' ? 'bg-[#f4e9dc] border-[#e8d9c8]' : ''}`}>Resting (no pressure)</button>
                      </div>
                    </div>

                    <div className="mt-5 flex justify-end gap-3">
                      <button onClick={() => setShowNewThread(false)} className="btn btn-ghost">Cancel</button>
                      <button onClick={handleCreateThread} className="btn btn-warm" disabled={!newThreadText.trim()}>Hold this thread</button>
                    </div>
                    <div className="text-[11px] text-[#6b625b] mt-4">One sentence is complete. You can return in five minutes or five days. The engine keeps the question alive.</div>
                  </motion.div>
                </div>
              )}
            </AnimatePresence>

            {/* Focused Curiosity Engine for a single thread */}
            {activeThreadId && activeThread && (
              <div className="mb-8">
                <div className="flex items-center justify-between mb-3">
                  <button onClick={closeEngine} className="text-sm flex items-center gap-1 text-[#6b625b] hover:text-[#2c2522]">
                    ← Back to all threads
                  </button>
                  <div className="flex gap-2">
                    <button onClick={() => enterZen(activeThreadId)} className="btn btn-ghost text-xs">Enter focus mode</button>
                    <button onClick={() => { closeEngine(); setView('taxonomy'); }} className="btn btn-ghost text-xs">Weave into taxonomy</button>
                  </div>
                </div>

                <div className="card p-7">
                  <div className="text-xs uppercase tracking-widest text-[#6b625b] mb-1 flex items-center gap-2">
                    CAPTURED {formatDate(activeThread.created_at)}
                    {activeThread.status === 'resting' && <span className="pill">resting</span>}
                  </div>
                  <div className="thread-text text-[17px] leading-snug">{activeThread.initial_text}</div>

                  {activeTaxonForThread && (
                    <div className="mt-3 text-xs text-[#c17f3a]">Woven into: {activeTaxonForThread.name}</div>
                  )}

                  {/* Reflections + the engine itself */}
                  <div className="mt-8">
                    <div className="section-title mb-3">Returns &amp; reflections</div>

                    {activeReflections.length === 0 && (
                      <div className="text-sm text-[#6b625b] mb-4">No reflections yet. This is normal. Come back when the heat has left your body.</div>
                    )}

                    <div className="space-y-4">
                      {activeReflections.map((r, idx) => (
                        <div key={r.id} className="border-l-2 border-[#d8d0c4] pl-4 text-[15px]">
                          <div className="text-xs text-[#6b625b] mb-1">{formatDate(r.created_at)} · {formatTime(r.created_at)}</div>
                          <div className="reflection-text whitespace-pre-wrap">{r.content}</div>
                          {r.moves && (
                            <div className="mt-2 flex flex-wrap gap-1.5">
                              {r.moves.frame && <span className="move-badge">frame: {r.moves.frame}</span>}
                              {r.moves.reciprocity && <span className="move-badge">reciprocity: {r.moves.reciprocity}</span>}
                              {r.moves.threshold_note && <span className="move-badge">threshold noted</span>}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* The reflection composer — heart of the ritual */}
                  <div className="mt-8 pt-6 border-t">
                    <div className="section-title mb-2">Add a reflection or repair script attempt</div>
                    <textarea
                      className="textarea"
                      placeholder="I went back two hours later and said the one sentence that actually mattered. She didn't answer but she didn't leave either."
                      value={reflectText}
                      onChange={e => setReflectText(e.target.value)}
                    />

                    {/* Optional structured moves — visible but never required */}
                    <div className="mt-3">
                      <button onClick={() => setShowMoves(!showMoves)} className="text-xs text-[#c17f3a] hover:underline">
                        {showMoves ? 'Hide' : 'Log an optional move'} (cooperation threshold, reciprocity, frame choice…)
                      </button>

                      {showMoves && (
                        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                          <div>
                            <div className="text-xs text-[#6b625b] mb-1">Frame you were in</div>
                            <div className="flex gap-2">
                              {(['curious', 'accountable'] as const).map(f => (
                                <button key={f} onClick={() => setMove(m => ({ ...m, frame: f }))}
                                  className={`pill ${move.frame === f ? 'bg-[#f4e9dc] border-[#e8d9c8]' : ''}`}>{f}</button>
                              ))}
                            </div>
                          </div>
                          <div>
                            <div className="text-xs text-[#6b625b] mb-1">What reciprocity actually happened?</div>
                            <select className="input text-sm" value={move.reciprocity || ''} onChange={e => setMove(m => ({ ...m, reciprocity: e.target.value as any }))}>
                              <option value="">—</option>
                              <option value="immediate">Immediate openness</option>
                              <option value="later_reciprocated">Later reciprocated</option>
                              <option value="delayed">Delayed / took time</option>
                              <option value="defensive">Defensive or withdrawn</option>
                              <option value="none">None visible</option>
                            </select>
                          </div>
                          <div className="md:col-span-2">
                            <div className="text-xs text-[#6b625b] mb-1">Cooperation threshold — what did you (or they) seem to need?</div>
                            <input className="input" placeholder="I needed the evening to pass and a walk before I could imagine bringing it up without heat."
                              value={move.threshold_note || ''} onChange={e => setMove(m => ({ ...m, threshold_note: e.target.value }))} />
                          </div>
                          <div className="md:col-span-2">
                            <div className="text-xs text-[#6b625b] mb-1">Outcome or norm note (what you think this reinforced)</div>
                            <input className="input" placeholder="We seem to be practicing “pause before the second sentence”."
                              value={move.outcome_note || ''} onChange={e => setMove(m => ({ ...m, outcome_note: e.target.value }))} />
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="mt-4 flex gap-3">
                      <button onClick={handleAddReflection} disabled={!reflectText.trim()} className="btn btn-warm">Save reflection</button>
                      <button onClick={() => { setReflectText(''); setMove({}); setShowMoves(false); }} className="btn btn-ghost">Clear</button>
                    </div>
                    <div className="text-[11px] text-[#6b625b] mt-2">Nothing here is required. The only data that exists is what you chose to write.</div>
                  </div>

                  {/* Quick script use from this thread */}
                  {scripts.length > 0 && (
                    <div className="mt-8 pt-6 border-t">
                      <div className="section-title mb-2">Use a repair script from your library</div>
                      <div className="flex flex-wrap gap-2">
                        {scripts.slice(0, 6).map(s => (
                          <button key={s.id} onClick={() => handleUseScript(s.id, activeThreadId, undefined)}
                            className="text-xs px-3 py-1 rounded-full border border-[#d8d0c4] hover:bg-white">
                            {s.title}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* List of threads */}
            {!activeThreadId && (
              <>
                <div className="flex items-center justify-between mb-3">
                  <div className="section-title">Threads you are holding</div>
                  <button onClick={() => setShowNewThread(true)} className="btn btn-primary text-sm"><Plus size={16} /> New thread</button>
                </div>

                {filteredThreads.length === 0 && (
                  <div className="empty-state">
                    No threads yet. The first one is the whole point.<br />
                    <button onClick={() => setShowNewThread(true)} className="mt-3 underline">Hold your first confusion</button>
                  </div>
                )}

                <div className="grid gap-3">
                  {filteredThreads.map(t => (
                    <div key={t.id} onClick={() => openThread(t.id)} className="card p-5 cursor-pointer group">
                      <div className="flex justify-between text-xs text-[#6b625b]">
                        <div>{formatDate(t.created_at)} · {t.reflection_count || 0} returns</div>
                        <div className="flex items-center gap-2">
                          {t.primary_taxon_name && <span className="pill">{t.primary_taxon_name}</span>}
                          <span className="pill">{t.status}</span>
                        </div>
                      </div>
                      <div className="mt-2 text-[15.2px] leading-snug group-hover:text-[#c17f3a] transition-colors">{short(t.initial_text, 220)}</div>
                      <div className="mt-3 text-xs text-[#c17f3a] flex items-center gap-1 opacity-0 group-hover:opacity-100 transition">Open in the Curiosity Engine <ArrowRight size={14} /></div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
        )}

        {/* TAXONOMY — the artifact */}
        {view === 'taxonomy' && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="section-title">Your Confusion Taxonomy</div>
                <div className="text-2xl tracking-tight">The cognitive artifact you actually leave with</div>
              </div>
              <button onClick={() => setShowNewTaxon(true)} className="btn btn-warm"><Plus size={16} /> New taxon</button>
            </div>

            {taxa.length === 0 && (
              <div className="empty-state mb-6">Your taxonomy is empty. Create one taxon, then weave threads into it. That is the whole practice.</div>
            )}

            <div className="grid md:grid-cols-2 gap-4">
              {taxa.map(t => (
                <div key={t.id} className="card p-5">
                  <div className="flex justify-between">
                    <div className="font-medium text-lg tracking-tight">{t.name}</div>
                    <button onClick={() => printTaxon(t)} className="text-xs text-[#6b625b] hover:text-[#2c2522] flex items-center gap-1"><BookOpen size={14} /> print</button>
                  </div>
                  {t.description && <div className="text-sm mt-1 text-[#5c6652]">{t.description}</div>}

                  <div className="mt-4 text-xs flex gap-4 text-[#6b625b]">
                    <div>{t.thread_count || 0} threads</div>
                    <div>{t.reflection_count || 0} reflections</div>
                  </div>

                  <div className="mt-4 flex gap-2">
                    <button onClick={() => { setView('threads'); /* user can assign from thread */ }} className="btn btn-ghost text-xs">Weave threads here</button>
                    <button onClick={() => { setEditingTaxon(t); setNewTaxonName(t.name); setNewTaxonDesc(t.description || ''); }} className="btn btn-ghost text-xs"><Edit2 size={14} /> edit</button>
                  </div>

                  {/* Simple pattern summary (from worker) */}
                  <div className="mt-4 pt-3 border-t text-xs text-[#6b625b]">
                    The patterns visible here come only from the moves you logged while reflecting.
                  </div>
                </div>
              ))}
            </div>

            {/* New taxon modal */}
            <AnimatePresence>
              {(showNewTaxon || editingTaxon) && (
                <div className="fixed inset-0 bg-black/30 z-[60] flex items-center justify-center p-5" onClick={() => { setShowNewTaxon(false); setEditingTaxon(null); }}>
                  <div onClick={e => e.stopPropagation()} className="modal w-full max-w-md p-6">
                    <div className="font-medium text-lg">{editingTaxon ? 'Edit taxon' : 'Create a new taxon'}</div>
                    <input className="input mt-4" placeholder="Name (e.g. After-school decompression storms)" value={newTaxonName} onChange={e => setNewTaxonName(e.target.value)} />
                    <textarea className="textarea mt-3" placeholder="Your description of this pattern…" value={newTaxonDesc} onChange={e => setNewTaxonDesc(e.target.value)} />
                    <div className="mt-4 flex gap-3 justify-end">
                      <button onClick={() => { setShowNewTaxon(false); setEditingTaxon(null); }} className="btn btn-ghost">Cancel</button>
                      <button onClick={async () => {
                        if (editingTaxon) {
                          await api.updateTaxon(editingTaxon.id, { name: newTaxonName, description: newTaxonDesc });
                          setEditingTaxon(null);
                        } else {
                          await handleCreateTaxon();
                        }
                        setNewTaxonName(''); setNewTaxonDesc('');
                        await refreshTaxa();
                      }} className="btn btn-warm" disabled={!newTaxonName.trim()}>Save</button>
                    </div>
                  </div>
                </div>
              )}
            </AnimatePresence>
          </div>
        )}

        {/* SCRIPTS — the repair scripting library */}
        {view === 'scripts' && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <div>
                <div className="section-title">Your Repair Scripts</div>
                <div className="text-xl tracking-tight">Living documents you actually use</div>
              </div>
              <button onClick={() => setShowNewScript(true)} className="btn btn-warm"><Plus size={16} /> New script</button>
            </div>

            {scripts.length === 0 && <div className="empty-state">No scripts yet. Create one from a reflection that felt useful. It will travel with you.</div>}

            <div className="space-y-3">
              {scripts.map(s => (
                <div key={s.id} className="card p-5">
                  <div className="flex justify-between">
                    <div className="font-medium">{s.title}</div>
                    <div className="text-xs text-[#6b625b]">{s.usage_count} uses</div>
                  </div>
                  {s.taxon_name && <div className="text-xs text-[#c17f3a] mt-0.5">{s.taxon_name}</div>}
                  <pre className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-[#3f3833]">{s.body}</pre>
                  <div className="mt-3 text-xs">
                    <button onClick={() => {
                      if (threads.length === 0) { toast.info('Create a thread first, then you can log that you used this script.'); return; }
                      // Simple: use on the most recent open thread
                      const target = threads.find(th => th.status === 'open') || threads[0];
                      handleUseScript(s.id, target.id);
                    }} className="text-[#c17f3a] hover:underline">Log use on a thread →</button>
                  </div>
                </div>
              ))}
            </div>

            {/* New script modal */}
            <AnimatePresence>
              {showNewScript && (
                <div className="fixed inset-0 bg-black/30 z-[60] flex items-center justify-center p-5" onClick={() => setShowNewScript(false)}>
                  <div onClick={e => e.stopPropagation()} className="modal w-full max-w-lg p-6">
                    <div className="font-medium">New repair script</div>
                    <input className="input mt-4" placeholder="Title (e.g. “Low-pressure invitation after a hard day”)" value={newScriptTitle} onChange={e => setNewScriptTitle(e.target.value)} />
                    <textarea className="textarea mt-3 h-40" placeholder={`I'd like to understand what happened earlier. I'm not coming in hot. When you're ready, even just a "not now" is fine.`} value={newScriptBody} onChange={e => setNewScriptBody(e.target.value)} />
                    <div className="mt-4 flex justify-end gap-3">
                      <button onClick={() => setShowNewScript(false)} className="btn btn-ghost">Cancel</button>
                      <button onClick={handleCreateScript} disabled={!newScriptTitle.trim() || !newScriptBody.trim()} className="btn btn-warm">Save to library</button>
                    </div>
                    <div className="text-xs text-[#6b625b] mt-3">These are for you. They help you enter the conversation from a place you chose, not the place the heat took you.</div>
                  </div>
                </div>
              )}
            </AnimatePresence>
          </div>
        )}

        {/* LOOM — norm emergence, visible through your own logged moves */}
        {view === 'loom' && (
          <div>
            <div className="mb-4">
              <div className="section-title">The Loom</div>
              <div className="text-2xl tracking-tight">Your moves over time. The patterns are yours.</div>
            </div>

            {patterns && (
              <div className="mb-6 grid md:grid-cols-3 gap-3 text-sm">
                <div className="card p-4">
                  <div className="text-xs text-[#6b625b]">Frames chosen</div>
                  <div className="mt-2 text-xs">Curious: {patterns.frame_counts?.curious || 0} · Accountable: {patterns.frame_counts?.accountable || 0}</div>
                </div>
                <div className="card p-4">
                  <div className="text-xs text-[#6b625b]">Reciprocity you observed</div>
                  <div className="mt-1 text-xs">{Object.entries(patterns.reciprocity_counts || {}).map(([k, v]) => `${k}: ${v}`).join(' · ') || 'None logged yet'}</div>
                </div>
                <div className="card p-4">
                  <div className="text-xs text-[#6b625b]">Taxa you return to most</div>
                  <div className="mt-1 text-xs">{(patterns.top_taxa_by_returns || []).map((t: any) => t.name).join(', ') || '—'}</div>
                </div>
              </div>
            )}

            <div className="section-title mb-3">Chronological moves</div>
            {loomEvents.length === 0 && <div className="empty-state">The Loom is empty until you log reflections with moves. That is by design.</div>}

            <div className="space-y-2">
              {loomEvents.map(ev => (
                <div key={ev.id} className="loom-line text-sm py-1">
                  <span className="text-[#6b625b] tabular-nums">{formatDate(ev.created_at)}</span>
                  <span className="mx-2 text-[#d8d0c4]">·</span>
                  <span className="font-medium">{ev.event_type}</span>
                  {ev.taxon_name && <span className="ml-2 text-[#c17f3a]">in {ev.taxon_name}</span>}
                  {ev.thread_initial && <div className="text-xs text-[#5c6652] mt-0.5 pl-1">{short(ev.thread_initial)}</div>}
                  {ev.payload && (ev.payload.frame || ev.payload.reciprocity || ev.payload.threshold_note) && (
                    <div className="text-xs mt-0.5 pl-1 text-[#9c6644]">
                      {ev.payload.frame && `frame: ${ev.payload.frame} `}
                      {ev.payload.reciprocity && `· reciprocity: ${ev.payload.reciprocity} `}
                      {ev.payload.threshold_note && `· threshold: ${short(ev.payload.threshold_note, 70)}`}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* PRIVACY + EXPORT + DELETE — full controls */}
        {view === 'privacy' && (
          <div className="max-w-2xl">
            <div className="section-title mb-1">Privacy &amp; sovereignty</div>
            <div className="text-2xl tracking-tight mb-4">Everything here is yours. Export it. Delete it. Never share it.</div>

            <div className="card p-6 mb-5">
              <div className="font-medium mb-2">Your access token (the only identity)</div>
              <div className="font-mono text-xs bg-[#f1ede5] p-3 rounded break-all">{token}</div>
              <div className="text-xs text-[#6b625b] mt-2">Copy this to use curio on another device. There is no account recovery. This is the feature.</div>
            </div>

            <div className="card p-6 mb-5">
              <div className="font-medium mb-2">Optional email (only for inbound threads via Email Routing)</div>
              <div className="flex gap-2">
                <input className="input" placeholder="you@yourdomain.com" value={emailInput} onChange={e => setEmailInput(e.target.value)} />
                <button onClick={async () => { if (emailInput) { await api.updateEmail(emailInput); toast.success('Email saved. Use it in Cloudflare Email Routing rules.'); } }} className="btn btn-ghost">Save</button>
              </div>
              <div className="text-xs text-[#6b625b] mt-2">If you set up Email Routing to forward mail to this Worker, emails from this address become new threads automatically. Extremely useful at 11pm.</div>
            </div>

            <div className="card p-6">
              <div className="font-medium mb-3">Private export (Workflow + R2)</div>
              <p className="text-sm text-[#5c6652]">A durable Workflow collects everything, builds a beautiful readable codex (Markdown), and writes multiple files to your private R2 prefix. You control them.</p>

              <button onClick={handleRequestExport} disabled={exporting} className="btn btn-warm mt-4">
                {exporting ? 'Starting Workflow…' : 'Request full export'}
              </button>

              <div className="mt-6">
                <div className="text-xs uppercase tracking-widest text-[#6b625b] mb-2">Your exports</div>
                {exportsList.length === 0 && <div className="text-sm text-[#6b625b]">None yet. Exports appear here once the Workflow finishes.</div>}
                {exportsList.map((ex: any) => (
                  <div key={ex.id} className="flex items-center justify-between text-sm py-2 border-b last:border-0">
                    <div>
                      <div>{formatDate(ex.created_at)} — {ex.status}</div>
                      <div className="text-xs text-[#6b625b] font-mono">{ex.r2_prefix}</div>
                    </div>
                    <div className="flex gap-2">
                      {ex.status === 'complete' && (
                        <>
                          <button onClick={() => handleDownload(ex.id, 'codex.md')} className="btn btn-ghost text-xs"><Download size={14} /> codex</button>
                          <button onClick={() => handleDownload(ex.id, 'full.json')} className="btn btn-ghost text-xs">json</button>
                        </>
                      )}
                      <button onClick={() => handleDeleteExport(ex.id)} className="text-xs text-[#9c6644] hover:text-red-600"><Trash2 size={15} /></button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-8 p-5 border border-red-200 bg-white rounded-xl">
              <div className="font-medium text-red-900">Nuclear option</div>
              <div className="text-sm text-red-800 mt-1">Delete every row in D1 for your token and every object in R2 under your export prefix. Irreversible. No one can recover it.</div>
              <button onClick={handleFullDelete} className="mt-3 text-sm px-4 py-1.5 rounded bg-red-100 text-red-900 hover:bg-red-200">Delete all my data permanently</button>
            </div>

            <div className="mt-8 text-xs text-[#6b625b]">
              Links to the maker (intentionally everywhere):<br />
              <a href="https://www.linkedin.com/in/mindful-abhay/" className="link" target="_blank">https://www.linkedin.com/in/mindful-abhay/</a><br />
              <a href="https://buymeacoffee.com/abhaypratapsingh" className="link" target="_blank">https://buymeacoffee.com/abhaypratapsingh</a>
            </div>
          </div>
        )}
      </div>

      {/* Command palette */}
      <AnimatePresence>
        {cmdOpen && (
          <div className="fixed inset-0 z-[70] bg-black/40 flex items-start justify-center pt-24" onClick={() => setCmdOpen(false)}>
            <div onClick={e => e.stopPropagation()} className="w-full max-w-md bg-white rounded-xl border shadow-xl overflow-hidden">
              <input autoFocus value={cmdQuery} onChange={e => setCmdQuery(e.target.value)} placeholder="Type a command or view…" className="w-full px-4 py-3 text-lg border-b outline-none" />
              <div className="max-h-72 overflow-auto py-1">
                {cmdActions.map((a, i) => (
                  <div key={i} onClick={() => runCmdAction(a)} className="px-4 py-2.5 hover:bg-[#f8f4ec] cursor-pointer text-sm flex items-center gap-2">
                    <ArrowRight size={15} className="text-[#c17f3a]" /> {a.label}
                  </div>
                ))}
                {cmdActions.length === 0 && <div className="px-4 py-3 text-sm text-[#6b625b]">No matches. Try “taxonomy”, “export”, or “new thread”.</div>}
              </div>
              <div className="text-[10px] text-[#6b625b] px-4 py-2 border-t">esc to close · n for new thread · ? to open this</div>
            </div>
          </div>
        )}
      </AnimatePresence>

      {/* Zen / focus mode overlay */}
      <AnimatePresence>
        {zenThreadId && activeThread && (
          <div className="fixed inset-0 z-[80] zen p-6 md:p-10 overflow-auto" onClick={() => setZenThreadId(null)}>
            <div className="max-w-2xl mx-auto" onClick={e => e.stopPropagation()}>
              <button onClick={() => setZenThreadId(null)} className="text-xs mb-4 text-[#6b625b]">exit focus</button>
              <div className="text-sm text-[#6b625b] mb-1">Focus mode — one thread, one question</div>
              <div className="text-2xl tracking-tight mb-6">{activeThread.initial_text}</div>

              {activeReflections.map(r => (
                <div key={r.id} className="mb-6 pl-4 border-l-2 border-[#d8d0c4]">
                  <div className="text-xs text-[#6b625b]">{formatDate(r.created_at)}</div>
                  <div className="mt-1 text-[15.5px] leading-relaxed">{r.content}</div>
                </div>
              ))}

              <div className="mt-8">
                <textarea className="textarea" placeholder="What feels true right now, in this quieter state?" value={reflectText} onChange={e => setReflectText(e.target.value)} />
                <button onClick={async () => { await handleAddReflection(); setZenThreadId(null); }} disabled={!reflectText.trim()} className="mt-3 btn btn-warm">Save &amp; leave focus</button>
              </div>
            </div>
          </div>
        )}
      </AnimatePresence>

      {/* Footer with maker links on every page */}
      <div className="border-t border-[#d8d0c4] mt-12 py-6 text-xs text-center text-[#6b625b]">
        curio is a public-utility tool. No data leaves your token. No scores. No nudges.<br />
        Made by <a href="https://www.linkedin.com/in/mindful-abhay/" className="link" target="_blank">Abhay Pratap Singh</a> · <a href="https://buymeacoffee.com/abhaypratapsingh" className="link" target="_blank">buy me a coffee</a>
      </div>
    </div>
  );
}
